import Big from "big.js";
import { division, isDefined } from "../../../../../../lib/common";
import { RecognitionRateData } from "../../../../../reference-source-data";
import { User } from "../../../../../reference-source-data/user-v2";
import { SegmentPurchaseIntent, SegmentPurchaseIntentBig } from "../../types";
import { changeBigPurchaseIntent, changeNumberPurchaseIntent } from "../../../purchase-intent/func";
import { PurchaseIntentSoreBig, Constant, TrialBigResponse, UserSize, RecognitionRate, TrialResponse } from "./types";

/**
 * (A’) トライアル購入数量 (補正前)
 * - A. トライアル率の計算
 * 1. (ブランドユーザのトライアル率) = [ブランドユーザの購入意向] x [ブランドユーザ認知率] x ＜定数C1＞
 * 2. (ブランドノンユーザのトライアル率) = [ブランドノンユーザの購入意向] x [ブランドノンユーザ認知率] x ＜定数C2＞
 * 3. (カテゴリノンユーザのトライアル率) = [ブランドノンユーザの購入意向] x [カテゴリノンユーザ認知率] x ＜定数C3＞
 *- B. トライアル購入数量の計算
 * 4. (ブランドユーザのトライアル購入数量) = [ブランドユーザ数] x (ブランドユーザのトライアル率)
 * 5. (ブランドノンユーザのトライアル購入数量) = [ブランドノンユーザ数] x (ブランドノンユーザのトライアル率)
 * 6. (カテゴリノンユーザのトライアル購入数量) = [カテゴリノンユーザ数] x (カテゴリノンユーザのトライアル率)
 * - C. トライアル数量の合計
 * @param {SegmentPurchaseIntentBig} purchaseIntent 購入意向
 * @param {RecognitionAndUserData} recognitionRateData 認知率 & ユーザ率データ
 * @param {User} user ユーザ数（ブランド） or ユーザ数（カテゴリ）
 * @param {Constant} constant 定数
 * @returns {TrialResponse}
 */
export const calcTrial = (
	purchaseIntent: PurchaseIntentSoreBig,
	recognitionRateData: RecognitionRateData,
	user: User,
	constant: Constant,
): TrialBigResponse => {
	const userSize: UserSize = {
		brandUser: user.gSize_BrandUser,
		brandNoneUser: user.gSize_BrandNonuser,
		categoryNoneUser: user.gSize_CategoryNonuser,
	};
	const recognitionRate: RecognitionRate = {
		brandUser: recognitionRateData.gAware_BrandUser,
		brandNoneUser: recognitionRateData.gAware_BrandNonuser,
		categoryNoneUser: recognitionRateData.gAware_CategoryNonuser,
	};
	// A. トライアル率の計算
	const brandUserTrialRate = purchaseIntent.brandUser.mul(recognitionRate.brandUser).mul(constant.c1);
	const brandNoneUserTrialRate = purchaseIntent.brandNoneUser.mul(recognitionRate.brandNoneUser).mul(constant.c2);
	const categoryNoneUserTrialRate = purchaseIntent.categoryNoneUser
		.mul(recognitionRate.categoryNoneUser)
		.mul(constant.c3);
	// B. トライアル購入数量の計算
	const brandUserTrial = brandUserTrialRate.mul(userSize.brandUser);
	const brandNoneUserTrial = brandNoneUserTrialRate.mul(userSize.brandNoneUser);
	const categoryNoneUserTrial = categoryNoneUserTrialRate.mul(userSize.categoryNoneUser);
	// C. トライアル数量の合計
	const trial = brandUserTrial.add(brandNoneUserTrial).add(categoryNoneUserTrial);
	return {
		// A. トライアル率の計算
		brandUserTrialRate,
		brandNoneUserTrialRate,
		categoryNoneUserTrialRate,
		// B. トライアル購入数量の計算
		brandUserTrial,
		brandNoneUserTrial,
		categoryNoneUserTrial,
		// C. トライアル数量の合計
		trial,
	};
};

export const changeBigTrialResponse = (trialResponse: TrialResponse): TrialBigResponse => {
	const {
		brandUserTrialRate,
		brandNoneUserTrialRate,
		categoryNoneUserTrialRate,
		brandUserTrial,
		brandNoneUserTrial,
		categoryNoneUserTrial,
		trial,
	} = trialResponse;
	return {
		brandUserTrialRate: new Big(brandUserTrialRate),
		brandNoneUserTrialRate: new Big(brandNoneUserTrialRate),
		categoryNoneUserTrialRate: new Big(categoryNoneUserTrialRate),
		brandUserTrial: new Big(brandUserTrial),
		brandNoneUserTrial: new Big(brandNoneUserTrial),
		categoryNoneUserTrial: new Big(categoryNoneUserTrial),
		trial: new Big(trial),
	};
};

export const changeNumberTrialResponse = (trialResponse: TrialBigResponse): TrialResponse => {
	const {
		brandUserTrialRate,
		brandNoneUserTrialRate,
		categoryNoneUserTrialRate,
		brandUserTrial,
		brandNoneUserTrial,
		categoryNoneUserTrial,
		trial,
	} = trialResponse;
	return {
		brandUserTrialRate: brandUserTrialRate.toNumber(),
		brandNoneUserTrialRate: brandNoneUserTrialRate.toNumber(),
		categoryNoneUserTrialRate: categoryNoneUserTrialRate.toNumber(),
		brandUserTrial: brandUserTrial.toNumber(),
		brandNoneUserTrial: brandNoneUserTrial.toNumber(),
		categoryNoneUserTrial: categoryNoneUserTrial.toNumber(),
		trial: trial.toNumber(),
	};
};

/**
 * カテゴリ人口 x 現行品の購入率からトライアル数量を求める
 * @param {Big} repPopulation
 * @param {number | Big | undefined} currentProductPurchaseRate
 * @returns {Big}
 */
export const calcCurrentProductTrial = (
	repPopulation: Big,
	currentProductPurchaseRate: number | Big | undefined,
): Big | undefined =>
	isDefined(currentProductPurchaseRate) ? repPopulation.mul(currentProductPurchaseRate).div(100) : undefined;
/**
 * @param {Big} currentProductTrialSalesVolume 現行品トライアル売上数量
 * @param {Big} currentProductTrial 現行品のトライアル購入数量(補正前)
 */
export const calcCorrectionFactor = (currentProductTrialSalesVolume: Big, currentProductTrial: Big) =>
	isDefined(currentProductTrialSalesVolume) && isDefined(currentProductTrial)
		? division(currentProductTrialSalesVolume, currentProductTrial)
		: undefined;
/**
 * (A)トライアル購入数量 (補正後)
 * @param {Big} testProductTrial テスト品のトライアル購入数量(補正前)
 * @param {Big} correctionFactor 補正係数
 * @returns {Big}
 */
export const calcCorrectionTrial = (testProductTrial: Big, correctionFactor: Big): Big => {
	return testProductTrial.mul(correctionFactor);
};

export const changeBigSegmentPurchaseIntent = (purchaseIntent: SegmentPurchaseIntent): SegmentPurchaseIntentBig => {
	const { brandUser, brandNoneUser, categoryNoneUser, categoryUser } = purchaseIntent;
	return {
		brandUser: changeBigPurchaseIntent(brandUser),
		brandNoneUser: changeBigPurchaseIntent(brandNoneUser),
		categoryNoneUser: changeBigPurchaseIntent(categoryNoneUser),
		categoryUser: changeBigPurchaseIntent(categoryUser),
	};
};

export const getScoreSegmentPurchaseIntent = (purchaseIntent: SegmentPurchaseIntentBig): PurchaseIntentSoreBig => {
	const { brandUser, brandNoneUser, categoryNoneUser } = purchaseIntent;
	return {
		brandNoneUser: brandNoneUser.score,
		brandUser: brandUser.score,
		categoryNoneUser: categoryNoneUser.score,
	};
};

export const changeNumberSegmentPurchaseIntent = (purchaseIntent: SegmentPurchaseIntentBig): SegmentPurchaseIntent => {
	const { brandUser, brandNoneUser, categoryNoneUser, categoryUser } = purchaseIntent;
	return {
		brandUser: changeNumberPurchaseIntent(brandUser),
		brandNoneUser: changeNumberPurchaseIntent(brandNoneUser),
		categoryNoneUser: changeNumberPurchaseIntent(categoryNoneUser),
		categoryUser: changeNumberPurchaseIntent(categoryUser),
	};
};
